import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { CalendarClock, CheckCircle2, XCircle, Briefcase } from "lucide-react";

/** Convert Date → YYYY-MM-DD (local). */
const iso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/**
 * AvailabilityCalendar — displays booked / available / off dates for a professional or team.
 *
 * Props:
 *  - userId (professional id) OR teamId — provide exactly one
 *  - editable (bool): if true and userId matches the logged-in user, allow toggling available/off.
 */
export default function AvailabilityCalendar({ userId, teamId, editable = false, testIdPrefix = "avail" }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [available, setAvailable] = useState(new Set());
  const [off, setOff] = useState(new Set());
  const booked = useMemo(() => new Set(data?.booked_dates || []), [data]);

  const load = async () => {
    try {
      const path = userId ? `/professionals/${userId}/availability-calendar` : `/teams/${teamId}/availability-calendar`;
      const { data } = await api.get(path);
      setData(data);
      setAvailable(new Set(data.available_dates || []));
      setOff(new Set(data.unavailable_dates || []));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId, teamId]);

  const toggleDate = (day, kind) => {
    const key = iso(day);
    if (booked.has(key)) { toast.info("This date is already booked — remove/complete the booking to change."); return; }
    if (kind === "available") {
      const next = new Set(available);
      if (next.has(key)) next.delete(key); else { next.add(key); off.delete(key); }
      setAvailable(next);
      setOff(new Set(off));
    } else {
      const next = new Set(off);
      if (next.has(key)) next.delete(key); else { next.add(key); available.delete(key); }
      setOff(next);
      setAvailable(new Set(available));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/staff/availability", {
        available_dates: Array.from(available),
        unavailable_dates: Array.from(off),
      });
      toast.success("Availability saved.");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!data) return <div className="text-sm text-white/60 font-manrope">Loading calendar…</div>;

  const modifiers = {
    booked: (d) => booked.has(iso(d)),
    available: (d) => available.has(iso(d)) && !booked.has(iso(d)),
    off: (d) => off.has(iso(d)) && !booked.has(iso(d)),
  };
  const modifiersClassNames = {
    booked: "bg-red-100 text-red-700 line-through font-outfit font-bold",
    available: "bg-gold-100 text-gold-600 font-outfit font-bold",
    off: "bg-slate-200 text-white/60 font-outfit font-bold",
  };

  return (
    <div className="card-flat p-5 space-y-3" data-testid={`${testIdPrefix}-calendar`}>
      <div className="flex items-center gap-2">
        <CalendarClock className="w-4 h-4 text-brand" />
        <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold">Availability calendar</div>
      </div>

      <div className="flex flex-wrap gap-3 text-xs font-manrope text-white/60">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-gold-200"></span> Available</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-red-200"></span> Booked</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-slate-200"></span> Off</span>
      </div>

      {editable ? (
        <>
          <Calendar
            mode="single"
            selected={undefined}
            onSelect={undefined}
            onDayClick={(d, mods, ev) => {
              // Shift-click marks day as "off"; plain click marks as "available"
              toggleDate(d, ev?.shiftKey ? "off" : "available");
            }}
            modifiers={modifiers}
            modifiersClassNames={modifiersClassNames}
            className="rounded-md border border-white/10 bg-[#0A0A0A]"
            data-testid={`${testIdPrefix}-calendar-editor`}
          />
          <div className="text-[11px] text-white/60 font-manrope">
            Tap a date to mark it <span className="text-gold-600 font-semibold">available</span>. Shift-tap (or long-press on mobile) to mark it <span className="text-white/60 font-semibold">off</span>. Booked dates are locked.
          </div>
          <Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand-hover text-white w-full font-manrope font-semibold" data-testid={`${testIdPrefix}-save`}>
            {saving ? "Saving…" : "Save availability"}
          </Button>
        </>
      ) : (
        <Calendar
          mode="single"
          selected={undefined}
          onSelect={undefined}
          modifiers={modifiers}
          modifiersClassNames={modifiersClassNames}
          className="rounded-md border border-white/10 bg-[#0A0A0A] pointer-events-none"
          data-testid={`${testIdPrefix}-calendar-view`}
        />
      )}

      <div className="grid grid-cols-3 gap-2 text-center text-xs font-outfit pt-2 border-t border-white/10">
        <div><div className="text-gold-500 font-bold text-xl">{available.size}</div><div className="text-white/60 uppercase tracking-widest"><CheckCircle2 className="w-3 h-3 inline mr-1" />Available</div></div>
        <div><div className="text-red-600 font-bold text-xl">{booked.size}</div><div className="text-white/60 uppercase tracking-widest"><Briefcase className="w-3 h-3 inline mr-1" />Booked</div></div>
        <div><div className="text-white/70 font-bold text-xl">{off.size}</div><div className="text-white/60 uppercase tracking-widest"><XCircle className="w-3 h-3 inline mr-1" />Off</div></div>
      </div>
    </div>
  );
}
